import { Tag } from "./tag";

export interface CreateTicketFormData {
  id?: number; // Optional ID
  title: string;
  description: string;
  status: string;
  customerName: string;
  phoneNumber?: string;
  source: string;
  group: string;
  type: string;
  priority: string;
  category: string;
  assignedTo?: string;
  tags: string[]; // Updated to an array
  attachments?: File[];
  isPublic: boolean;
  customerId?: number; // Added optional customerId field
  updatedById?: number; // Added optional updatedById
  formTemplateId?: number; // Form template ID for dynamic forms
  formResponse?: any; // Form response data (camelCase for frontend)
  form_response?: any; // Form response data (snake_case for API)
}

export interface TicketAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size: number | null;
  uploaded_by_id: number;
  created_at: string;
  preview_url?: string;
}

export interface Ticket {
  id: number;
  title: string;
  description: string;
  customer?: TicketCustomer;
  customer_id?: number;
  'customer.id'?: number;
  'customer.full_name': string;
  'customer.external_ref'?: string;
  'customer.phone'?: string;
  'customer.deleted_at'?: string;
  source_id?: number;
  category_id?: number;
  type_id?: number;
  group_id?: number;
  priority: string;
  status: string;
  form_template_id?: number;
  form_response?: any;
  created_by?: TicketUser;
  assigned_to?: TicketUser;
  updated_by?: TicketUser;
  created_by_id?: number;
  assigned_to_id?: number;
  updated_by_id?: number; // Added updated_by_id
  is_public: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
  'createdBy.email'?: string;
  'createdBy.fullName'?: string;
  'createdBy.role'?: string;
  'createdBy.pfp_url'?: string;
  'assignedTo.email'?: string;
  'assignedTo.fullName'?: string;
  'assignedTo.role'?: string;
  'assignedTo.pfp_url'?: string;
  'updatedBy.email'?: string; // Added updatedBy email
  'updatedBy.fullName'?: string; // Added updatedBy full name
  'updatedBy.role'?: string; // Added updatedBy role
  'updatedBy.pfp_url'?: string; // Added updatedBy profile picture URL
  source_name?: string;
  group_name?: string;
  type_name?: string;
  category_name?: string;
  createdAt: string;
  updatedAt: string;
  tags: Tag[];
  attachments?: TicketAttachment[];
  attachment_count?: number;
  attachment_files?: any[];
  files?: any[];
}

interface TicketCustomer {
  id: number;
  full_name: string;
  external_ref?: string;
  phone?: string;
  deleted_at?: string;
}

interface TicketUser {
  id: number;
  email: string;
  full_name: string;
  role: 'admin' | 'agent' | 'requester';
  pfp_url?: string; // Profile picture URL
  phone?: string;
  isMasterUser?: boolean;
  isPlatformAdmin?: boolean;
}

// Transform function to convert API response to frontend format
export function transformTicketResponse(ticket: Ticket): Ticket & { customer?: TicketCustomer; created_by?: TicketUser; assigned_to?: TicketUser; updated_by?: TicketUser } {
  return {
    ...ticket,
    customer: ticket.customer_id ? {
      id: ticket.customer_id,
      full_name: ticket['customer.full_name'] || 'Unknown',
      external_ref: ticket['customer.external_ref'] || '',
      phone: ticket['customer.phone'] || '',
      deleted_at: ticket['customer.deleted_at'],
    } : undefined,
    created_by: ticket.created_by_id ? {
      id: ticket.created_by_id,
      email: ticket['createdBy.email'] || '',
      full_name: ticket['createdBy.fullName'] || 'Unknown',
      role: 'requester', // Default role since it's not in the response
      pfp_url: ticket['createdBy.pfp_url'],
    } : undefined,
    assigned_to: ticket.assigned_to_id ? {
      id: ticket.assigned_to_id,
      email: ticket['assignedTo.email'] || '',
      full_name: ticket['assignedTo.fullName'] || 'Unknown',
      role: 'requester',
      pfp_url: ticket['assignedTo.pfp_url'],
    } : undefined,
    updated_by: ticket.updated_by_id ? {
      id: ticket.updated_by_id,
      email: ticket['updatedBy.email'] || '',
      full_name: ticket['updatedBy.fullName'] || 'Unknown',
      role: 'requester',
      pfp_url: ticket['updatedBy.pfp_url'],
    } : undefined
  };
}